import { EMAIL_CHANGE_CANCEL_PATH } from "./account/paths.js";
import { SITE_ORIGIN } from "./config.js";
import { PRODUCT_NAME } from "./legal/company.js";

/**
 * Canonical and Open Graph metadata, written into <head> at route time.
 *
 * The site is one bundle behind a catch-all `_redirects` rule: every route is
 * served from the same `index.html`, so its <head> carries the hub's title and
 * no canonical link at all until a view sets one. Two rules hold here.
 *
 * 1. **The origin is configured, never observed.** `location.origin` is
 *    whatever host served the bundle — a preview deploy, a headless check
 *    fulfilling from `dist/`, `live.` before its 301. A page that canonicalises
 *    itself to one of those teaches a crawler the wrong home. Everything
 *    composes {@link SITE_ORIGIN} (`config.ts`), which is the one definition of
 *    the site's address.
 * 2. **`og:url` and `<link rel=canonical>` are the same string**, because they
 *    are the same claim. They are produced together by {@link metaTags} rather
 *    than written at two call sites.
 *
 * The tag list is built purely so it can be asserted under vitest's node
 * environment; {@link applyMeta} is the only part that touches the DOM.
 */

export interface PageMeta {
  /** Document title. Also `og:title`. */
  title: string;
  description: string;
  /** Route path, e.g. `/about`. Made absolute against {@link SITE_ORIGIN}. */
  path: string;
  /**
   * Keep the page out of search results (`<meta name="robots" content="noindex">`).
   * For pages that only make sense from a mailed link, such as the e-mail-change
   * cancel page. `public/_headers` sends `X-Robots-Tag` for the same paths, for a
   * crawler that never runs the bundle.
   */
  noindex?: boolean;
}

/**
 * The absolute, canonical address of a route.
 *
 * Query and fragment are dropped: a canonical URL names the DOCUMENT, and
 * `/about?utm_source=newsletter` is the same document as `/about`. A trailing
 * slash is dropped for the same reason — one address per page — except at the
 * root, which is a bare `/`.
 */
export function canonicalUrl(path: string): string {
  const clean = path.split("#")[0]?.split("?")[0] ?? "";
  const withSlash = clean.startsWith("/") ? clean : `/${clean}`;
  const trimmed = withSlash.replace(/\/+$/, "");
  return `${SITE_ORIGIN}${trimmed === "" ? "/" : trimmed}`;
}

/**
 * The share card (1200x630, rendered by `brand/render_brand.py` into
 * `public/brand/`). Route-invariant: every page shares under the brand card.
 * `index.html` repeats it for crawlers, which never run the bundle.
 */
export const OG_IMAGE_PATH = "/brand/og-image.png";

/** One head entry. `canonical` is the link element; everything else is a meta. */
export interface MetaTag {
  key: string;
  content: string;
}

export function metaTags(meta: PageMeta): MetaTag[] {
  const url = canonicalUrl(meta.path);
  return [
    { key: "canonical", content: url },
    { key: "description", content: meta.description },
    { key: "og:type", content: "website" },
    { key: "og:site_name", content: PRODUCT_NAME },
    { key: "og:title", content: meta.title },
    { key: "og:description", content: meta.description },
    { key: "og:url", content: url },
    { key: "og:image", content: `${SITE_ORIGIN}${OG_IMAGE_PATH}` },
    { key: "twitter:card", content: "summary_large_image" },
    { key: "twitter:image", content: `${SITE_ORIGIN}${OG_IMAGE_PATH}` },
    { key: "twitter:title", content: meta.title },
    { key: "twitter:description", content: meta.description },
    ...(meta.noindex === true ? [{ key: "robots", content: "noindex" }] : []),
  ];
}

/**
 * Write a route's metadata into <head>, replacing whatever the previous route
 * left there. Every tag this module owns is marked `data-page-meta` so a
 * navigation removes exactly its own entries and never the shell's charset,
 * viewport, icon or stylesheet links.
 */
export function applyMeta(meta: PageMeta): void {
  const head = document.head;
  for (const stale of head.querySelectorAll("[data-page-meta]")) stale.remove();
  document.title = meta.title;
  for (const tag of metaTags(meta)) {
    const el =
      tag.key === "canonical"
        ? Object.assign(document.createElement("link"), { rel: "canonical", href: tag.content })
        : Object.assign(document.createElement("meta"), { content: tag.content });
    if (tag.key !== "canonical") {
      // `og:*` is an RDFa property, `description`/`twitter:*` are names. Using
      // the wrong attribute is silently ignored by every scraper.
      el.setAttribute(tag.key.startsWith("og:") ? "property" : "name", tag.key);
    }
    el.setAttribute("data-page-meta", "");
    head.append(el);
  }
}

/** The hub. Title mirrors `index.html`'s so the shell and the route agree. */
export const HUB_META: PageMeta = {
  title: "Arrow Chess Live — Live tournaments",
  description: "Every tournament on the board right now, with live boards, clocks, evaluations and commentary.",
  path: "/",
};

export const ABOUT_META: PageMeta = {
  title: "About Arrow Chess",
  description:
    "Arrow Chess is a chess app from Omega Algorithms Ltd: live elite tournaments, a daily puzzle ladder and offline play. No ads, privacy-first.",
  path: "/about",
};

export const SEARCH_META: PageMeta = {
  title: "Search — Arrow Chess Live",
  description: "Search finished tournaments, players and games from the Arrow Chess archive.",
  path: "/search",
};

export const PLAYERS_META: PageMeta = {
  title: "Players — Arrow Chess Live",
  description: "Find any player: ratings, titles, federations and every game in the Arrow Chess archive.",
  path: "/players",
};

export const CREDITS_META: PageMeta = {
  title: "Credits — Arrow Chess Live",
  description: "The data, artwork, software and photographers behind Arrow Chess.",
  path: "/credits",
};

export const PRIVACY_META: PageMeta = {
  title: "Privacy Policy — Arrow Chess Live",
  description: "How Arrow Chess and Omega Algorithms Ltd handle personal data.",
  path: "/privacy",
};

export const TERMS_META: PageMeta = {
  title: "Terms of Service — Arrow Chess Live",
  description: "The terms for using the Arrow Chess site.",
  path: "/terms",
};

/** The e-mail-change cancel page (#3549): reached only from a mailed link, so never indexed. */
export const EMAIL_CHANGE_CANCEL_META: PageMeta = {
  title: "Cancel an email change — Arrow Chess",
  description: "Cancel a pending change to the email address on your Arrow Chess account.",
  path: EMAIL_CHANGE_CANCEL_PATH,
  noindex: true,
};

/**
 * The pages that state their own title and description. Every static route in
 * `routes.ts` has an entry here — `meta.test.ts` reads that table and checks —
 * so no page borrows the hub's "Live tournaments" title in a tab or a share.
 */
const PAGE_META: Record<string, PageMeta> = {
  "/": HUB_META,
  "/about": ABOUT_META,
  "/search": SEARCH_META,
  "/players": PLAYERS_META,
  "/players/leaderboard": {
    title: "Player leaderboard — Arrow Chess",
    description: "Explore the FIDE top 100 in classical, rapid and blitz, with ratings and player profiles.",
    path: "/players/leaderboard",
  },
  "/credits": CREDITS_META,
  "/privacy": PRIVACY_META,
  "/terms": TERMS_META,
  [EMAIL_CHANGE_CANCEL_PATH]: EMAIL_CHANGE_CANCEL_META,
};

/**
 * The archive routes' metadata, by path PREFIX.
 *
 * A route with an id in it cannot have a table entry, and the words that WOULD
 * be specific — the tournament's name, the two players — are not known until
 * the catalog answers. Writing them later, per view, is the failure `applyMeta`
 * exists to prevent: a view that forgets inherits the previous page's canonical
 * URL. So the shape is honest instead — the kind of page is stated here and the
 * canonical URL is this route's own, which is the part a crawler needs.
 */
const PREFIX_META: { prefix: string; title: string; description: string }[] = [
  {
    prefix: "/edition/",
    title: "Tournament — Arrow Chess Live",
    description: "One tournament: its live boards, every round, results and standings, game by game.",
  },
  {
    prefix: "/game/",
    title: "Game — Arrow Chess Live",
    description: "A finished game: the moves, engine evaluations, per-move commentary and the recap.",
  },
  {
    prefix: "/player/",
    title: "Player — Arrow Chess Live",
    description: "One player: ratings, career record, head-to-heads and notable games.",
  },
  {
    prefix: "/round/",
    title: "Round — Arrow Chess Live",
    description: "Every board of one round — live while it’s on, and kept once it’s over.",
  },
];

/**
 * The metadata for a path — the ONE place a route becomes a <head>.
 *
 * The router applies this on every render rather than each view doing it,
 * because the failure mode of per-view metadata is a route that forgets: it
 * then inherits the PREVIOUS page's canonical URL, which is a worse claim than
 * having none. A path with no entry of its own still gets its own canonical
 * address; only the words are the site's general ones, which is exactly what
 * those pages showed before this existed.
 */
export const metaForPath = (path: string): PageMeta => {
  const clean = path.replace(/\/+$/, "") || "/";
  const exact = PAGE_META[clean];
  if (exact !== undefined) return exact;
  for (const entry of PREFIX_META) {
    if (clean.startsWith(entry.prefix)) return { title: entry.title, description: entry.description, path };
  }
  return { title: HUB_META.title, description: HUB_META.description, path };
};
